// Built-in TypeScript/JavaScript analyzer (ARCHITECTURE.md §6.3, §6.8).
//
// Uses the TypeScript Compiler API purely as a parser: ts.createSourceFile
// builds an AST from raw source — no module resolution, no type checking, and
// crucially no code execution. Parser-specific types (ts.SourceFile, ts.Node)
// stay internal; the output is the language-independent FileAnalysisResult IR.
//
// All edges emitted here are deterministic (confidence 1.0): the analyzer only
// records what the syntax explicitly states (§6.9). Path alias (@/...) and
// cross-file resolution are out of scope for this phase.

import ts from "typescript";

import type { GraphEdge } from "../../types.js";
import type {
  AnalyzerCapability,
  FileAnalysisResult,
  LanguageAnalyzer,
  SymbolInfo,
  SymbolKind,
} from "../types.js";

const FILE_EXTENSIONS = [
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  ".mts",
  ".cts",
  ".mjs",
  ".cjs",
] as const;

const CAPABILITIES: AnalyzerCapability[] = [
  "symbols",
  "imports",
  "exports",
  "calls",
  "type_references",
];

/** Choose the parser dialect from the file extension. */
function scriptKindFor(filePath: string): ts.ScriptKind {
  if (filePath.endsWith(".tsx")) return ts.ScriptKind.TSX;
  if (filePath.endsWith(".jsx")) return ts.ScriptKind.JSX;
  if (
    filePath.endsWith(".js") ||
    filePath.endsWith(".mjs") ||
    filePath.endsWith(".cjs")
  ) {
    return ts.ScriptKind.JS;
  }
  return ts.ScriptKind.TS;
}

export class TypeScriptAnalyzer implements LanguageAnalyzer {
  readonly language = "typescript";
  readonly capabilities = CAPABILITIES;
  readonly fileExtensions = FILE_EXTENSIONS;

  async analyze(filePath: string, content: string): Promise<FileAnalysisResult> {
    const start = performance.now();

    const sourceFile = ts.createSourceFile(
      filePath,
      content,
      ts.ScriptTarget.Latest,
      /* setParentNodes */ true,
      scriptKindFor(filePath),
    );

    const symbols: SymbolInfo[] = [];
    const edges: GraphEdge[] = [];

    // Named exports without a module specifier (`export { a, b }`) mark
    // previously-declared local symbols as exported.
    const reExportedNames = new Set<string>();

    const lineOf = (node: ts.Node): number =>
      sourceFile.getLineAndCharacterOfPosition(node.getStart(sourceFile)).line +
      1;

    // Like lineOf, but extends the start line backward to include any leading
    // comment trivia (JSDoc / line comments) directly attached to the node.
    // Used only for the symbol `line` field so symbol-section ContextPacks keep
    // leading documentation; edge evidence keeps the exact lineOf position.
    const commentAwareLineOf = (node: ts.Node): number => {
      const commentRanges = ts.getLeadingCommentRanges(
        sourceFile.text,
        node.getFullStart(),
      );
      if (commentRanges && commentRanges.length > 0) {
        return (
          sourceFile.getLineAndCharacterOfPosition(commentRanges[0].pos).line +
          1
        );
      }
      return lineOf(node);
    };

    const endLineOf = (node: ts.Node): number =>
      sourceFile.getLineAndCharacterOfPosition(node.getEnd()).line + 1;

    const hasExportModifier = (node: ts.Node): boolean =>
      ts.canHaveModifiers(node)
        ? (ts.getModifiers(node) ?? []).some(
            (m) => m.kind === ts.SyntaxKind.ExportKeyword,
          )
        : false;

    const isPrivateOrProtected = (node: ts.Node): boolean =>
      ts.canHaveModifiers(node)
        ? (ts.getModifiers(node) ?? []).some(
            (m) =>
              m.kind === ts.SyntaxKind.PrivateKeyword ||
              m.kind === ts.SyntaxKind.ProtectedKeyword,
          )
        : false;

    /** Compose a qualified name: "Class" + "method" → "Class.method". */
    const qualify = (prefix: string | undefined, name: string): string =>
      prefix ? `${prefix}.${name}` : name;

    /** Render a function-like declaration's signature: "(a: number): void". */
    const signatureOf = (node: ts.SignatureDeclarationBase): string => {
      const params = node.parameters
        .map((p) => p.getText(sourceFile))
        .join(", ");
      const ret = node.type ? `: ${node.type.getText(sourceFile)}` : "";
      return `(${params})${ret}`;
    };

    /** Resolve a member's declared name to a string, when statically known. */
    const memberName = (name: ts.PropertyName): string | null => {
      if (
        ts.isIdentifier(name) ||
        ts.isPrivateIdentifier(name) ||
        ts.isStringLiteral(name) ||
        ts.isNumericLiteral(name)
      ) {
        return name.text;
      }
      return null;
    };

    /** Callee name of a call expression, when it is a plain or member call. */
    const calleeNameOf = (expr: ts.Expression): string | null => {
      if (ts.isIdentifier(expr)) return expr.text;
      if (ts.isPropertyAccessExpression(expr)) return expr.name.text;
      return null;
    };

    const addImportEdge = (specifier: string, line: number): void => {
      edges.push({
        from: filePath,
        to: specifier,
        kind: "imports",
        classification: "deterministic",
        evidence: [
          {
            type: "static_import",
            detail: `import from '${specifier}'`,
            line,
            confidence: 1,
          },
        ],
      });
    };

    const addReExportEdge = (specifier: string, line: number): void => {
      edges.push({
        from: filePath,
        to: specifier,
        kind: "re_exports",
        classification: "deterministic",
        evidence: [
          {
            type: "static_import",
            detail: `re-export from '${specifier}'`,
            line,
            confidence: 1,
          },
        ],
      });
    };

    const addTypeReferenceEdge = (
      fromId: string,
      typeName: string,
      detail: string,
      line: number,
    ): void => {
      edges.push({
        from: fromId,
        to: typeName,
        kind: "type_reference",
        classification: "deterministic",
        evidence: [{ type: "static_import", detail, line, confidence: 1 }],
      });
    };

    const addCallEdge = (
      fromId: string,
      calleeName: string,
      line: number,
    ): void => {
      edges.push({
        from: fromId,
        to: calleeName,
        kind: "calls",
        classification: "deterministic",
        evidence: [
          {
            type: "static_import",
            detail: `calls ${calleeName}`,
            line,
            confidence: 1,
          },
        ],
      });
    };

    // Create a SymbolInfo (with stable symbolId) and return its symbolId.
    const addSymbol = (opts: {
      name: string;
      kind: SymbolKind;
      exported: boolean;
      node: ts.Node;
      qualifiedName: string;
      parentSymbolId?: string;
      signature?: string;
    }): string => {
      const symbolId = `${filePath}#${opts.qualifiedName}`;
      const symbol: SymbolInfo = {
        name: opts.name,
        kind: opts.kind,
        exported: opts.exported,
        line: commentAwareLineOf(opts.node),
        endLine: endLineOf(opts.node),
        symbolId,
      };
      if (opts.parentSymbolId) symbol.parentSymbolId = opts.parentSymbolId;
      if (opts.signature) symbol.signature = opts.signature;
      symbols.push(symbol);
      return symbolId;
    };

    // Heritage clauses (`extends`, `implements`) → type_reference edges,
    // emitted from the declaring symbol (symbol → type name).
    const recordHeritage = (
      fromId: string,
      clauses: ts.NodeArray<ts.HeritageClause> | undefined,
    ): void => {
      if (!clauses) return;
      for (const clause of clauses) {
        const keyword =
          clause.token === ts.SyntaxKind.ExtendsKeyword
            ? "extends"
            : "implements";
        for (const typeExpr of clause.types) {
          const typeName = typeExpr.expression.getText(sourceFile);
          addTypeReferenceEdge(
            fromId,
            typeName,
            `${keyword} ${typeName}`,
            lineOf(typeExpr),
          );
        }
      }
    };

    // Imports and (re-)exports are module-level statements; collect them in a
    // single top-level pass. Symbol/call extraction happens in the recursive
    // walk below so nested declarations and class members are covered too.
    for (const statement of sourceFile.statements) {
      if (
        ts.isImportDeclaration(statement) &&
        ts.isStringLiteral(statement.moduleSpecifier)
      ) {
        addImportEdge(statement.moduleSpecifier.text, lineOf(statement));
      } else if (ts.isExportDeclaration(statement)) {
        if (
          statement.moduleSpecifier &&
          ts.isStringLiteral(statement.moduleSpecifier)
        ) {
          addReExportEdge(statement.moduleSpecifier.text, lineOf(statement));
        } else if (
          statement.exportClause &&
          ts.isNamedExports(statement.exportClause)
        ) {
          for (const element of statement.exportClause.elements) {
            reExportedNames.add(element.name.text);
          }
        }
      }
    }

    // Context threaded through the recursive walk.
    interface VisitCtx {
      // Prefix for child symbol qualified names (e.g. "Class" or "outer.fn").
      scopePrefix?: string;
      // parentSymbolId for direct members (set only inside class/interface).
      containerSymbolId?: string;
      // Export status of the enclosing class/interface (for member inference).
      containerExported?: boolean;
      // symbolId used as `from` for calls edges (nearest function-like scope).
      enclosingSymbolId?: string;
    }

    const visit = (node: ts.Node, ctx: VisitCtx): void => {
      // Function call → calls edge from the enclosing symbol (or the file when
      // the call sits in module-level code).
      if (ts.isCallExpression(node)) {
        const callee = calleeNameOf(node.expression);
        if (callee) {
          addCallEdge(ctx.enclosingSymbolId ?? filePath, callee, lineOf(node));
        }
        node.forEachChild((child) => visit(child, ctx));
        return;
      }

      if (ts.isVariableStatement(node)) {
        const exported = hasExportModifier(node);
        for (const decl of node.declarationList.declarations) {
          const init = decl.initializer;
          if (!ts.isIdentifier(decl.name)) {
            if (init) visit(init, ctx);
            continue;
          }
          const name = decl.name.text;
          const fnInit =
            init && (ts.isArrowFunction(init) || ts.isFunctionExpression(init))
              ? init
              : undefined;
          const qn = qualify(ctx.scopePrefix, name);
          const symbolId = addSymbol({
            name,
            kind: "variable",
            exported,
            node: decl,
            qualifiedName: qn,
            parentSymbolId: ctx.containerSymbolId,
            signature: fnInit ? signatureOf(fnInit) : undefined,
          });
          if (init) {
            visit(
              init,
              fnInit ? { scopePrefix: qn, enclosingSymbolId: symbolId } : ctx,
            );
          }
        }
        return;
      }

      if (ts.isFunctionDeclaration(node) && node.name) {
        const qn = qualify(ctx.scopePrefix, node.name.text);
        const symbolId = addSymbol({
          name: node.name.text,
          kind: "function",
          exported: hasExportModifier(node),
          node,
          qualifiedName: qn,
          parentSymbolId: ctx.containerSymbolId,
          signature: signatureOf(node),
        });
        node.forEachChild((child) =>
          visit(child, { scopePrefix: qn, enclosingSymbolId: symbolId }),
        );
        return;
      }

      if (ts.isClassDeclaration(node) && node.name) {
        const exported = hasExportModifier(node);
        const qn = qualify(ctx.scopePrefix, node.name.text);
        const symbolId = addSymbol({
          name: node.name.text,
          kind: "class",
          exported,
          node,
          qualifiedName: qn,
          parentSymbolId: ctx.containerSymbolId,
        });
        recordHeritage(symbolId, node.heritageClauses);
        node.forEachChild((child) =>
          visit(child, {
            scopePrefix: qn,
            containerSymbolId: symbolId,
            containerExported: exported,
            enclosingSymbolId: ctx.enclosingSymbolId,
          }),
        );
        return;
      }

      if (ts.isInterfaceDeclaration(node)) {
        const exported = hasExportModifier(node);
        const qn = qualify(ctx.scopePrefix, node.name.text);
        const symbolId = addSymbol({
          name: node.name.text,
          kind: "interface",
          exported,
          node,
          qualifiedName: qn,
          parentSymbolId: ctx.containerSymbolId,
        });
        recordHeritage(symbolId, node.heritageClauses);
        node.forEachChild((child) =>
          visit(child, {
            scopePrefix: qn,
            containerSymbolId: symbolId,
            containerExported: exported,
          }),
        );
        return;
      }

      if (ts.isTypeAliasDeclaration(node)) {
        addSymbol({
          name: node.name.text,
          kind: "type",
          exported: hasExportModifier(node),
          node,
          qualifiedName: qualify(ctx.scopePrefix, node.name.text),
          parentSymbolId: ctx.containerSymbolId,
        });
        return;
      }

      if (ts.isEnumDeclaration(node)) {
        addSymbol({
          name: node.name.text,
          kind: "enum",
          exported: hasExportModifier(node),
          node,
          qualifiedName: qualify(ctx.scopePrefix, node.name.text),
          parentSymbolId: ctx.containerSymbolId,
        });
        return;
      }

      // Class members (only inside a class body: containerSymbolId is set).
      if (
        ctx.containerSymbolId !== undefined &&
        (ts.isMethodDeclaration(node) ||
          ts.isGetAccessorDeclaration(node) ||
          ts.isSetAccessorDeclaration(node))
      ) {
        const name = memberName(node.name);
        if (name) {
          const qn = qualify(ctx.scopePrefix, name);
          const symbolId = addSymbol({
            name,
            kind: "function",
            exported:
              (ctx.containerExported ?? false) && !isPrivateOrProtected(node),
            node,
            qualifiedName: qn,
            parentSymbolId: ctx.containerSymbolId,
            signature: signatureOf(node),
          });
          node.forEachChild((child) =>
            visit(child, { scopePrefix: qn, enclosingSymbolId: symbolId }),
          );
          return;
        }
      }

      if (
        ctx.containerSymbolId !== undefined &&
        ts.isConstructorDeclaration(node)
      ) {
        const qn = qualify(ctx.scopePrefix, "constructor");
        const symbolId = addSymbol({
          name: "constructor",
          kind: "function",
          exported: ctx.containerExported ?? false,
          node,
          qualifiedName: qn,
          parentSymbolId: ctx.containerSymbolId,
          signature: signatureOf(node),
        });
        node.forEachChild((child) =>
          visit(child, { scopePrefix: qn, enclosingSymbolId: symbolId }),
        );
        return;
      }

      // Interface members (signatures): no bodies, so no call scope.
      if (
        ctx.containerSymbolId !== undefined &&
        (ts.isMethodSignature(node) || ts.isPropertySignature(node))
      ) {
        const name = memberName(node.name);
        if (name) {
          const isMethod = ts.isMethodSignature(node);
          addSymbol({
            name,
            kind: isMethod ? "function" : "variable",
            exported: ctx.containerExported ?? false,
            node,
            qualifiedName: qualify(ctx.scopePrefix, name),
            parentSymbolId: ctx.containerSymbolId,
            signature: isMethod ? signatureOf(node) : undefined,
          });
          return;
        }
      }

      node.forEachChild((child) => visit(child, ctx));
    };

    sourceFile.forEachChild((node) => visit(node, {}));

    // Apply `export { a }` (no module specifier) to declared symbols.
    if (reExportedNames.size > 0) {
      for (const symbol of symbols) {
        if (reExportedNames.has(symbol.name)) {
          symbol.exported = true;
        }
      }
    }

    return {
      filePath,
      language: this.language,
      symbols,
      edges,
      metadata: {
        parserBackend: "typescript-compiler-api",
        parseTimeMs: performance.now() - start,
        lineCount: sourceFile.getLineAndCharacterOfPosition(content.length)
          .line + 1,
      },
    };
  }
}
